import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import NivoBarChart from '../../charts/NivoBarChart'
import NivoLineChart from '../../charts/NivoLineChart'
import { BasicTooltip } from '@nivo/tooltip'
import { GEO_COLORS } from '../../../types/Stats'
import type { ChartType } from '../../../types/Stats'
import { formatChartTooltip, AXIS_FMT } from '../../../utils/chartFormatting'
import { buildWrappedLegends, legendMarginBottom, AXIS_TITLE_HEIGHT } from '../../../utils/nivoLegend'
import './Chart.css'
import { useLocalizationLabels } from '../../../context/LocalizationLabelContext'
import useElementWidth from '../../../hooks/useElementWidth'

interface Props {
  type:        ChartType
  data:        Record<string, Record<string, number>>
  yearRange:   [number, number]
  metricName:  string
  sourceUrl:   string | null
  sourceName:  string
  licenseName:  string | null
  licenseUrl:   string | null
  unit:        string

}

// Nivo's line generator always bridges gaps between defined points (there is no
// "spanGaps: false" equivalent), so a missing year would otherwise be drawn as a
// straight interpolation instead of a visible break. Splitting each geo's data into
// one sub-series per contiguous run reproduces the break; all runs for a geo share
// its id-derived color and only the first is surfaced in the legend.
function splitAtGaps(geoId: string, points: { x: string; y: number | null }[]) {
  const segments: { x: string; y: number | null }[][] = []
  let current: { x: string; y: number | null }[] = []
  points.forEach((point) => {
    if (point.y === null || point.y === undefined) {
      if (current.length) segments.push(current)
      current = []
    } else {
      current.push(point)
    }
  })
  if (current.length) segments.push(current)
  return segments.map((segment, i) => ({ id: `${geoId} ${i}`, data: segment }))
}

function geoIdFromSeriesId(seriesId: string | number): string {
  return String(seriesId).split(' ')[0]
}

const StatsChart = memo(function StatsChart({
  type, data, yearRange, metricName, sourceUrl, sourceName, licenseName, licenseUrl, unit,
}: Props) {
  const { t } = useTranslation('mainStats')
  const { geoNameOptionLabels, statsLabels } = useLocalizationLabels()
  const unitLabel = statsLabels.units?.[unit]?.label ?? unit
  const unitShort = statsLabels.units?.[unit]?.unit  ?? unit
  const isPercent = unitShort.includes('%')

  const labels = useMemo(() => {
    const allKeys = Array.from(new Set(Object.values(data).flatMap(d => Object.keys(d))))
    return allKeys
      .map(Number)
      .filter(y => y >= yearRange[0] && y <= yearRange[1])
      .sort((a, b) => a - b)
      .map(String)
  }, [data, yearRange])

  const geoEntries = useMemo(
    () => Object.entries(data).map(([geo, values], i) => ({
      geo,
      label: geoNameOptionLabels[geo]?.label ?? geo,
      color: GEO_COLORS[i % GEO_COLORS.length],
      isPrimary: i === 0,
      values,
    })),
    [data, geoNameOptionLabels]
  )

  const colorMap = useMemo(
    () => Object.fromEntries(geoEntries.map((g) => [g.label, g.isPrimary ? g.color : `${g.color}66`])),
    [geoEntries]
  )

  const barData = useMemo(
    () => labels.map((year) => Object.fromEntries([
      ['year', year],
      ...geoEntries.map((g) => [g.label, g.values[year] ?? null]),
    ])),
    [labels, geoEntries]
  )

  const lineSeries = useMemo(
    () => geoEntries.flatMap((g) => splitAtGaps(g.label, labels.map((year) => ({ x: year, y: g.values[year] ?? null })))),
    [geoEntries, labels]
  )

  const axisLeftFormat = (v: number) => (isPercent ? `${AXIS_FMT.format(v)} %` : AXIS_FMT.format(v))

  const [chartWrapperRef, containerWidth] = useElementWidth<HTMLDivElement>()

  // xTitle is always set below, so the legend needs to start after both the tick
  // labels and the axis title to avoid overlapping either.
  const legendStartY = 55 + AXIS_TITLE_HEIGHT
  const { legends, rowCount: legendRowCount } = buildWrappedLegends(
    geoEntries.map((g) => ({ id: g.label, label: g.label, color: g.color })),
    { containerWidth: containerWidth || undefined, itemWidth: 170, startY: legendStartY }
  )
  const bottomMargin = legendMarginBottom(legendRowCount, { startY: legendStartY })

  // Metrics like a budget balance ("saldo") can go negative and can also run into the
  // millions - a fixed left margin either clips negative bars past the chart boundary
  // (Nivo's bar/line value scale defaults to min: 0) or crowds the axis title against
  // wide tick numbers, so size both from the actual data.
  const allValues = geoEntries.flatMap((g) => labels.map((year) => g.values[year]).filter((v): v is number => v !== null && v !== undefined))
  const maxAbsFormatted = allValues.length > 0 ? axisLeftFormat(Math.max(...allValues.map(Math.abs))) : '0'
  const leftMargin = Math.max(60, 25 + maxAbsFormatted.length * 8)

  return (
    <>
      {metricName && <p className="chart-title">{metricName}</p>}
      <div className="chart-wrapper" ref={chartWrapperRef}>
        {type === 'bar' ? (
          <NivoBarChart
            height='100%'
            data={barData}
            keys={geoEntries.map((g) => g.label)}
            indexBy='year'
            groupMode='grouped'
            valueScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            colors={(d) => colorMap[d.id]}
            xTitle={t('chart.axisYear')}
            yTitle={unit ? unitLabel : undefined}
            axisLeft={{ format: axisLeftFormat, legendOffset: -(leftMargin - 15) }}
            margin={{ top: 20, right: 20, bottom: bottomMargin, left: leftMargin }}
            legends={legends}
            tooltip={({ id, value, color }) => (
              <BasicTooltip id={`${id}: ${formatChartTooltip(Number(value), unitShort)}`} color={color} enableChip />
            )}
          />
        ) : (
          <NivoLineChart
            height='100%'
            data={lineSeries}
            colors={(d) => colorMap[geoIdFromSeriesId(d.id)]}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            enableSlices='x'
            xTitle={t('chart.axisYear')}
            yTitle={unit ? unitLabel : undefined}
            axisLeft={{ format: axisLeftFormat, legendOffset: -(leftMargin - 15) }}
            margin={{ top: 20, right: 20, bottom: bottomMargin, left: leftMargin }}
            legends={legends}
            sliceTooltip={({ slice }) => {
              const seen = new Set<string>()
              const rows = slice.points.filter((point) => {
                const geo = geoIdFromSeriesId(point.seriesId)
                if (seen.has(geo)) return false
                seen.add(geo)
                return true
              })
              return (
                <div style={{ background: 'rgba(0, 0, 0, 0.85)', color: '#ffffff', padding: 10, borderRadius: 4 }}>
                  <strong>{slice.points[0]?.data.x}</strong>
                  {rows.map((point) => (
                    <div key={point.id}>
                      {geoIdFromSeriesId(point.seriesId)}: {formatChartTooltip(Number(point.data.y), unitShort)}
                    </div>
                  ))}
                </div>
              )
            }}
          />
        )}
      </div>
      {sourceUrl && (
        <p className="chart-source">
          {t('chart.sourceLinkText')}{' '}
          <a href={sourceUrl} target="_blank" rel="noreferrer">{sourceName}</a>
          {licenseUrl && (
            <>
              {' ('}
              <a href={licenseUrl} target="_blank" rel="noreferrer">
                {t('chart.licenseLabel')}
              </a>
              {licenseName && `, ${licenseName}`}
              {')'}
            </>
          )}
        </p>
      )}
    </>
  )
})

export { StatsChart }
